import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApplyTenantTheme } from '@mlm/ui/lib/tenant-theme';
import { Layout } from './components/layout';
import { RequirePasswordChange } from './components/require-password-change';
import { useAuthStore } from './lib/auth-store';
import { getLmsEnabled, getTenantFlags, restoreSession, tenantLogoUrl } from './lib/api';
import { LoginPage } from './pages/login';
import { RegisterPage } from './pages/register';
import { ImpersonatePage } from './pages/impersonate';
import { DashboardPage } from './pages/dashboard';
import { CoursesPage } from './pages/courses';
import { CourseEditorPage } from './pages/course-editor';
import { CourseStatsPage } from './pages/course-stats';
import { CertificatesPage } from './pages/certificates';
import SubscriptionPlanPage from './pages/subscription-plan';
import { CouponsPage } from './pages/coupons';
import { RevenuePage } from './pages/revenue';
import { MessagesPage } from './pages/messages';
import { QaPage } from './pages/qa';
import { GradingPage } from './pages/grading';
import { WalletPage } from './pages/wallet';
import { PayoutsPage } from './pages/payouts';
import { ProfilePage } from './pages/profile';
import { NotificationsPage } from './pages/notifications';
import { ChangePasswordPage } from './pages/change-password';

export function App() {
  const token = useAuthStore((s) => s.token);
  const [booting, setBooting] = useState(true);
  const [lmsOn, setLmsOn] = useState<boolean | null>(null);

  // Creator Studio is a white-labelled surface: the tenant's theme tokens, name
  // and mark, from the same PUBLIC call the shell already makes for its flags.
  // Light only — this portal ships no dark set and no toggle, so re-resolving
  // for a mode nobody can pick would only risk a half-dark shell.
  const { data: flags } = useQuery({ queryKey: ['tenant-flags'], queryFn: getTenantFlags });
  const theme = flags?.theme ?? null;
  useApplyTenantTheme({
    theme,
    faviconUrl: theme?.faviconKey
      ? `${tenantLogoUrl('favicon')}?v=${encodeURIComponent(theme.faviconKey)}`
      : theme?.logoKey
        ? `${tenantLogoUrl('light')}?v=${encodeURIComponent(theme.logoKey)}`
        : (theme?.logoUrl ?? null),
  });
  // The baked VITE_BRAND title is a build-time guess; the tenant's real name
  // arrives with the flags and wins.
  useEffect(() => {
    if (flags?.tenantName) document.title = `${flags.tenantName} — Creator Studio`;
  }, [flags?.tenantName]);

  useEffect(() => {
    // The LMS master switch decides whether this portal exists for the tenant
    // at all (the API 404s every /creator route when it's off).
    getLmsEnabled()
      .then(setLmsOn)
      .catch(() => setLmsOn(false));
    if (token) {
      setBooting(false);
      return;
    }
    restoreSession().finally(() => setBooting(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (booting || lmsOn === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!lmsOn) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center text-muted-foreground">
        <div>
          <div className="text-lg font-semibold text-foreground">
            Creator Studio is not available
          </div>
          <p className="mt-1 text-sm">This site does not offer courses.</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      {/* Outside RequireAuth on purpose: this route is what CREATES the
          session (it exchanges the admin's one-time grant), so a token guard
          in front of it would bounce every impersonation to /login. */}
      <Route path="/impersonate" element={<ImpersonatePage />} />
      <Route element={<RequireAuth />}>
        {/* Reachable while `must_change_password` is set — the gate below
            redirects here, so it must live OUTSIDE the gate. */}
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route element={<RequirePasswordChange />}>
          <Route element={<Layout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:id" element={<CourseEditorPage />} />
            <Route path="/courses/:id/stats" element={<CourseStatsPage />} />
            <Route path="/certificates" element={<CertificatesPage />} />
            <Route path="/subscription-plan" element={<SubscriptionPlanPage />} />
            <Route path="/coupons" element={<CouponsPage />} />
            <Route path="/revenue" element={<RevenuePage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/questions" element={<QaPage />} />
            <Route path="/grading" element={<GradingPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/payouts" element={<PayoutsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            {/* No sidebar entry — the bell's "See all" is the way in. */}
            <Route path="/notifications" element={<NotificationsPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RequireAuth() {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <Outlet />;
}
